import moment from 'moment';
import { pluralize } from './format';

export function parseDate(
    t: Date | string | number | undefined | null,
    timezone: boolean = false,
): Date | null {
    if (!t) {
        return null;
    }

    if (t instanceof Date) {
        return t;
    }

    if (!timezone && typeof t === 'string' && t.endsWith('Z')) {
        t = t.slice(0, -1);
    }

    const date = new Date(t);
    if (isNaN(date.getTime())) {
        return null;
    }

    return date;
}

/**
 * Migrates a Date to UTC but keeping the same day, month and year.
 * It will ignore any time in the date.
 * @param date
 */
export function toUtcDate(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}

/**
 * Serializes a date as a UTC-midnight ISO string (e.g. `2026-06-02T00:00:00.000Z`),
 * keeping the same calendar day as the input regardless of the local timezone.
 *
 * Use this when persisting a "calendar day" (a date without a meaningful time of
 * day) instead of an instant, so it renders on the same day for every user.
 *
 * - `Date` inputs (typically from date pickers, which return local-time Dates) have
 *   their local Y/M/D rebuilt at UTC midnight via `toUtcDate`.
 * - String and number inputs are parsed as UTC.
 * - Falsy and invalid inputs return `''`.
 */
export function toUtcDateISOString(date?: Date | number | string | null): string {
    if (!date) return '';
    if (date instanceof Date) {
        if (isNaN(date.getTime())) return '';
        return toUtcDate(date).toISOString();
    }
    const m = moment.utc(date);
    if (!m.isValid()) return '';
    return m.toISOString();
}

export function isGoZeroTime(input: string): boolean {
    return input === '0001-01-01T00:00:00Z';
}

export function businessDaysBetween(from: Date, to: Date): number {
    if (to <= from) return 0;
    let count = 0;
    const day = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    for (; day < end; day.setDate(day.getDate() + 1)) {
        const weekday = day.getDay();
        if (weekday !== 0 && weekday !== 6) count++;
    }
    return count;
}

export function getItemPeriod(period?: { from?: string; to?: string }) {
    const from = period?.from ?? '';
    const to = period?.to ?? '';

    const bothZero = isGoZeroTime(from) && isGoZeroTime(to);
    const bothEqual = from === to;
    if (bothZero || bothEqual) {
        return '';
    }

    return `${formatPeriod(from)} - ${formatPeriod(to)}`;
}

export function formatPeriod(t: string, timezone = false) {
    if (isGoZeroTime(t)) {
        return 'now';
    }

    if (!timezone && typeof t === 'string' && t.endsWith('Z')) {
        t = t.slice(0, -1);
    }

    const date = moment(t);
    if (!date.isValid()) {
        return '-';
    }

    return date.format('MMM YYYY');
}

export function formatPeriodDuration(
    from: string | Date | moment.Moment | undefined,
    to: string | Date | moment.Moment | undefined,
    detailed?: boolean,
) {
    const now = moment();

    function parse(v: string | Date | moment.Moment | undefined): moment.Moment {
        let when: moment.Moment;
        if (moment.isMoment(v)) {
            when = v;
        } else if (!v) {
            when = now;
        } else {
            when = typeof v === 'string' && isGoZeroTime(v) ? now : moment.utc(v);
            if (!when.isValid()) {
                when = now;
            }
        }

        return detailed ? when : when.date(1);
    }

    const diff = moment.duration(parse(to).diff(parse(from)));

    const years = diff.years();
    const months = diff.months();

    const parts = [];
    if (years) parts.push(`${years} ${pluralize(years, 'year', 'years')}`);
    if (months) parts.push(`${months} ${pluralize(months, 'month', 'months')}`);

    if (detailed) {
        const days = diff.days();
        const hours = diff.hours();
        const minutes = diff.minutes();
        const seconds = diff.seconds();

        if (days) parts.push(`${days} ${pluralize(days, 'day', 'days')}`);
        if (!years && !months && days < 10 && hours)
            parts.push(`${hours} ${pluralize(hours, 'hour', 'hours')}`);
        if (!years && !months && !days && minutes)
            parts.push(`${minutes} ${pluralize(minutes, 'minute', 'minutes')}`);
        if (!parts.length && seconds) {
            parts.push(`${seconds} ${pluralize(seconds, 'second', 'seconds')}`);
        }
    }

    return parts.join(', ');
}
